import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Authorize } from "reauthorize";
import { Confirm, Loader, Dropdown } from "semantic-ui-react";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { ADMIN_ROLES, isSystemAdminUser } from "@teamprevent/common/auth/roles";
import { ConfirmModal } from "@teamprevent/common/confirmedButton/ConfirmModal";

import { ApplicationState } from "../../../../applicationState";
import { createEmployeeDataExtract } from "../../../../dataExtracts/actions";
import { CREATE_EMPLOYEE_DATA_EXTRACT } from "../../../../dataExtracts/actionTypes";
import { requestEmployeeSignUpEmail, resetEmployeeUserEmail } from "../../../../signUpRequest/actions";
import { REQUEST_EMPLOYEE_SIGNUP_EMAIL, RESET_EMPLOYEE_USER_EMAIL } from "../../../../signUpRequest/actionTypes";
import { EmployeeRecordDetailModel } from "../../model";
import { basePathSelector } from "../../selectors";
import { deleteEmployeeRecord, deactivateEmployeeRecord } from "../../actions";

import ActivateButton from "./ActivateButton";
import { MergeEmployeeRecordModal } from "./MergeEmployeeRecordModal";

import { push } from "redux-little-router";

interface Props {
    employeeRecord: EmployeeRecordDetailModel;
}

export const CoreDetailsButtons: React.FunctionComponent<Props> = ({ employeeRecord }) => {
    const dispatch = useDispatch();

    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState<boolean>(false);
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = React.useState<boolean>(false);
    const [isSearchModalOpen, setIsSearchModalOpen] = React.useState<boolean>(false);
    const [isDeactivateConfirmationModalOpen, setIsDeactivateConfirmationModalOpen] = React.useState<boolean>(false);

    const currentUser = useSelector((state: ApplicationState) => state.currentUser);
    const basePath = useSelector((state: ApplicationState) => basePathSelector(state));

    const createDataExtract = React.useCallback(() => {
        dispatch(createEmployeeDataExtract(employeeRecord.customerId, employeeRecord.id));
    }, [dispatch, employeeRecord]);

    const navigateToDataExtracts = React.useCallback(() => dispatch(push("/dataExtracts")), [dispatch]);

    const onDeleteClick = React.useCallback(() => setIsDeleteConfirmationModalOpen(true), []);

    const onDelete = React.useCallback(async () => {
        await dispatch(deleteEmployeeRecord(employeeRecord.id, basePath));
        setIsDeleteConfirmationModalOpen(false);
    }, [basePath, dispatch, employeeRecord.id]);

    const setIsDeleteConfirmationModalOpenFalse = React.useCallback(() => setIsDeleteConfirmationModalOpen(false), []);

    const onMerge = React.useCallback(() => setIsSearchModalOpen(true), []);
    const onMergeComplete = React.useCallback(() => setIsSearchModalOpen(false), []);

    const onDeactivateClick = React.useCallback(() => setIsDeactivateConfirmationModalOpen(true), []);
    const setIsDeactivateConfirmationModalOpenFalse = React.useCallback(() => setIsDeactivateConfirmationModalOpen(false), []);

    const onDeactivate =  React.useCallback(() => {
        dispatch(deactivateEmployeeRecord(employeeRecord.customerId, employeeRecord.id));
        setIsDeactivateConfirmationModalOpen(false);
    }, [dispatch, employeeRecord]);

    const downloadData = React.useCallback(async () => {
        await createDataExtract();
        navigateToDataExtracts();
    }, [createDataExtract, navigateToDataExtracts]);

    const requestSignUpEmail = React.useCallback(() => {
        dispatch(requestEmployeeSignUpEmail(employeeRecord.customerId, employeeRecord.id));
    }, [dispatch, employeeRecord]);

    const openResetEmailConfirmationModal = React.useCallback(() => setIsConfirmationModalOpen(true), []);

    const closeResetEmailConfirmationModal = React.useCallback(() => setIsConfirmationModalOpen(false), []);

    const resetEmail = React.useCallback( async () => {
        await dispatch(resetEmployeeUserEmail(employeeRecord.customerId, employeeRecord.id));
        setIsConfirmationModalOpen(false);
    }, [dispatch, employeeRecord]);

    const getResetPersonalEmailAddressMessage = React.useMemo(() => {
        const personalEmail = employeeRecord.personalEmailAddress;

        if (personalEmail) {
            return `Are you sure you want to reset email for ${personalEmail}?`;
        } else {
            return "Are you sure you want to reset emails for the employee record?";
        }
    }, [employeeRecord.personalEmailAddress]);

    if (!employeeRecord) {
        return <Loader active />;
    }

    return (
        <>
            <Dropdown button icon="ellipsis vertical" className="left floated red icon" floating pointing="top">
                <Dropdown.Menu>
                    <Authorize authorize={[ADMIN_ROLES.SENIOR_ADMINISTRATOR.name, ADMIN_ROLES.SYSTEM_ADMIN.name]}>
                        <Dropdown.Item text="Delete" icon="delete" onClick={onDeleteClick} />
                    </Authorize>
                    {isSystemAdminUser(currentUser) && <Dropdown.Item text="Merge" icon="check" onClick={onMerge} />}
                    {employeeRecord.active && <Dropdown.Item text="Deactivate" icon="power" onClick={onDeactivateClick} />}
                </Dropdown.Menu>
            </Dropdown>
            {!employeeRecord.active && <ActivateButton />}
            <Authorize authorize={[ADMIN_ROLES.SENIOR_ADMINISTRATOR.name, ADMIN_ROLES.SYSTEM_ADMIN.name]}>
                <IsLoadingButton
                    icon="download"
                    content="Download Data"
                    onClick={downloadData}
                    requestNames={CREATE_EMPLOYEE_DATA_EXTRACT}
                    secondary
                />
            </Authorize>
            <IsLoadingButton
                icon="mail"
                content="Send Invite"
                onClick={requestSignUpEmail}
                requestNames={REQUEST_EMPLOYEE_SIGNUP_EMAIL}
            />
            <IsLoadingButton
                icon="edit"
                content="Reset Email"
                onClick={openResetEmailConfirmationModal}
                requestNames={RESET_EMPLOYEE_USER_EMAIL}
            />
            <Confirm
                open={isConfirmationModalOpen}
                content={getResetPersonalEmailAddressMessage}
                onCancel={closeResetEmailConfirmationModal}
                onConfirm={resetEmail}
            />
            <ConfirmModal
                isOpen={isDeleteConfirmationModalOpen}
                promptText="Are you sure you want to delete this employee record? Type 'DELETE' and click confirm to delete."
                typeToConfirmText="DELETE"
                headingText="Delete Employee Record"
                onConfirm={onDelete}
                onClose={setIsDeleteConfirmationModalOpenFalse}
            />
            <MergeEmployeeRecordModal
                isOpen={isSearchModalOpen}
                onMergeComplete={onMergeComplete}
                employeeRecord={employeeRecord} />

            <ConfirmModal
                isOpen={isDeactivateConfirmationModalOpen}
                // eslint-disable-next-line max-len
                promptText="Are you sure you want to make this employee record inactive? Type 'DEACTIVATE' and click confirm to make the employee record inactive."
                headingText="Deactivate Employee Record"
                typeToConfirmText="DEACTIVATE"
                onConfirm={onDeactivate}
                onClose={setIsDeactivateConfirmationModalOpenFalse}
            />
        </>
    );
};
